import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../.././../config';
import './Demo.css';

const Demo = () => {
  // For navigation and URL parameters
  const navigate = useNavigate();
  const location = useLocation();
  
  // Scroll to top when component mounts
  useEffect(() => {
    // Immediately scroll to top when the component mounts
    window.scrollTo(0, 0);
  }, []);
  
  // URL validation
  useEffect(() => {
    // Get URL from query parameters
    const params = new URLSearchParams(location.search);
    const url = params.get('url');
    // Process URL from query params
    
    // Simplified URL validation to accept more domain formats
    // This will accept domains like 'randomsite.com', 'example.co.uk', etc.
    const simpleDomainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*(\.[a-zA-Z0-9][a-zA-Z0-9-]*)+$/;
    const pathOnlyRegex = /^\/[A-Za-z0-9\/_.-]*$/;
    
    // Test URL with regex
    
    // If URL is valid or provided, use it in the component
    if (url && (simpleDomainRegex.test(url) || pathOnlyRegex.test(url))) {
      // Valid URL found
      setHeroHighlight(url);
      setValidatedUrl(url);
      urlRef.current = url; // Update the ref
    } else if (url) {
      // If URL is provided but doesn't match regex, still use it
      // This ensures we use whatever domain is provided
      // Using provided URL despite regex mismatch
      setHeroHighlight(url);
      setValidatedUrl(url);
      urlRef.current = url; // Update the ref
    } else {
      // Only use default if no URL is provided
      const defaultUrl = 'example.com';
      // Using default URL
      setHeroHighlight(defaultUrl);
      setValidatedUrl(defaultUrl);
      urlRef.current = defaultUrl; // Update the ref
    }
  }, [location, navigate]);
  // State for the animated highlight text and URL
  const [isEditing, setIsEditing] = useState(false);
  const [heroHighlight, setHeroHighlight] = useState('example.com');
  const [validatedUrl, setValidatedUrl] = useState('example.com'); // Initialize with a default
  const [demoLink, setDemoLink] = useState(''); // State to store the demo link from API response
  
  // Use a ref to store the current URL value to avoid timing issues
  const urlRef = useRef('example.com');
  
  // Animation timeout reference
  const textAnimationRef = useRef(null);
  
  // Reference to the QR code section for scrolling
  const qrCodeSectionRef = useRef(null);
  
  // Custom smooth scroll function with adjustable duration
  const smoothScrollTo = (element, duration = 1500) => {
    if (!element) return;
    
    const targetPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime = null;
    
    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const run = easeInOutQuad(timeElapsed, startPosition, distance, duration);
      window.scrollTo(0, run);
      if (timeElapsed < duration) requestAnimationFrame(animation);
    };
    
    // Easing function for smoother animation
    const easeInOutQuad = (t, b, c, d) => {
      t /= d / 2;
      if (t < 1) return c / 2 * t * t + b;
      t--;
      return -c / 2 * (t * (t - 2) - 1) + b;
    };
    
    requestAnimationFrame(animation);
  };
  
  // Share message templates for social media
  const shareMessages = {
    default: "Check out my new AI chatbot created with AI Chat Assist! It's ready to transform customer engagement 24/7. Try it here: ",
    facebook: "I just launched a new AI chatbot with AI Chat Assist! It's answering questions and helping customers 24/7. Check it out: ",
    twitter: "My new #AIchatbot is live! Built with @AIChatAssist, it's handling customer queries instantly. Try it: ",
    linkedin: "Excited to share my new AI chatbot built with AI Chat Assist! It's transforming how we engage with customers by providing instant, intelligent responses 24/7. Connect with our business in a whole new way: ",
    whatsapp: "Hey! Check out the AI chatbot I just created. It's pretty amazing at answering questions instantly. Try it here: ",
    telegram: "I just launched this cool AI chatbot that can answer questions about our business 24/7. Give it a try: ",
    email: "I wanted to share my new AI chatbot with you. It provides instant answers to questions about our products and services. Experience it here: "
  };
  
  // Function to handle social media sharing
  const handleShare = (platform) => {
    const shareUrl = demoLink || validatedUrl || 'https://aichatassist.com/demo';
    const message = shareMessages[platform] || shareMessages.default;
    const encodedMessage = encodeURIComponent(message);
    const encodedUrl = encodeURIComponent(shareUrl);
    
    let shareLink = '';
    
    switch(platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedMessage}`;
        break;
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodedMessage}${encodedUrl}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&summary=${encodedMessage}`;
        break;
      case 'whatsapp':
        shareLink = `https://wa.me/?text=${encodedMessage}${encodedUrl}`;
        break;
      case 'telegram':
        shareLink = `https://t.me/share/url?url=${encodedUrl}&text=${encodedMessage}`;
        break;
      case 'email':
        shareLink = `mailto:?subject=${encodeURIComponent('Check out my new AI chatbot!')}&body=${encodedMessage}${encodedUrl}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(`${shareUrl}`)
          .then(() => {
            // Show a temporary success message
            const shareDropdown = document.getElementById('shareDropdown');
            if (shareDropdown) {
              const originalText = shareDropdown.innerHTML;
              shareDropdown.innerHTML = '<i class="bi bi-check-circle me-2"></i>Copied!';
              setTimeout(() => {
                shareDropdown.innerHTML = originalText;
              }, 2000);
            }
          })
          .catch(err => console.error('Failed to copy: ', err));
        return; // Don't open a new window for copy action
      default:
        return;
    }
    
    // Open share link in a new window
    if (shareLink) {
      window.open(shareLink, '_blank', 'width=600,height=400');
    }
  };
  
  // Define timeline steps with their data
  const timelineSteps = [
    {
      id: 'completed',
      title: 'Kickstart Your Bot',
      description: 'Captured your brand voice to create a perfectly tailored AI solution!',
      status: 'Completed',
      icon: 'bi-check',
      statusClass: 'status-completed'
    },
    {
      id: 'processed',
      title: 'Craft the Experience',
      description: 'Engineered stunning conversation flows with lightning-fast, seamless API integrations!',
      status: 'Completed',
      icon: 'bi-check',
      statusClass: 'status-completed'
    },
    {
      id: 'triggered',
      title: 'Launch the Demo',
      description: 'Your AI chatbot is live, transforming engagement with 24/7 intelligent assistance!',
      status: 'Completed',
      icon: 'bi-check',
      statusClass: 'status-completed'
    }
  ];
  
  // Create state for each step using the template data
  const [steps, setSteps] = useState(
    timelineSteps.map(step => ({
      ...step,
      state: 'default',
      currentDescription: '' // Start with empty descriptions
    }))
  );
  
  // Typing animation refs
  const typingRef = useRef(null);
  const animationRef = useRef(null);
  const timeoutRefs = useRef([]);
  const currentStepRef = useRef(0);
  
  // Animation speed settings
  const animationSettings = {
    // Timeline animation settings
    typeSpeed: 50,        // ms per character for typing
    stepDelay: 2500,      // delay before activating a step
    nextStepDelay: 1000,  // delay before starting the next step
    
    // Title edit animation settings
    editDelay: 3000,      // Time between edit animations
    cursorBlinkSpeed: 500 // Speed of cursor blink
  };
  
  // Helper function to get the correct class name based on state
  const getIconClassName = (baseClass, state) => {
    if (state === 'default') return baseClass;
    if (state === 'progress') return `${baseClass} progress`;
    if (state === 'active') return `${baseClass} active`;
    if (state === 'error') return `${baseClass} error`;
    return baseClass;
  };

  // Helper function to get the status text based on state
  const getStatusText = (defaultText, state) => {
    if (state === 'default') return 'Pending';
    if (state === 'progress') return 'Processing...';
    if (state === 'active') return 'Completed';
    if (state === 'error') return 'Failed';
    return defaultText;
  };
  
  // Helper function to get the status class based on state
  const getStatusClass = (baseClass, state) => {
    if (state === 'default') return 'status-pending';
    if (state === 'progress') return 'status-processing';
    if (state === 'active') return 'status-completed';
    if (state === 'error') return 'status-error';
    return baseClass;
  };
  
  // Helper function to get the title style based on state
  const getTitleStyle = (state) => {
    if (state === 'default' || state === 'progress') {
      return { color: '#888' }; // Gray color for incomplete steps
    }
    if (state === 'error') {
      return { color: '#dc3545' }; // Red color for error steps
    }
    return {}; // Default styling for active steps
  };
  
  // Helper function to determine if description should be visible
  const isDescriptionVisible = (state) => {
    return state === 'active' || state === 'error';
  };
  
  // Helper function to determine if all steps are completed
  const areAllStepsCompleted = () => {
    return steps.every(step => step.state === 'active');
  };
  // Text edit animation function
  const startEditAnimation = () => {
    // Toggle editing state
    setIsEditing(true);
    
    // Schedule to turn off editing after a delay
    setTimeout(() => {
      setIsEditing(false);
      
      // Schedule the next animation
      textAnimationRef.current = setTimeout(startEditAnimation, animationSettings.editDelay);
    }, animationSettings.cursorBlinkSpeed * 6);
  };
  
  // Typing animation function
  const typeText = (text, stepIndex, callback) => {
    let i = 0;
    const originalText = text;
    
    // Create a new steps array with empty description for the current step
    setSteps(prevSteps => {
      const newSteps = [...prevSteps];
      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        currentDescription: ''
      };
      return newSteps;
    });
    
    // For immediate display without animation in development
    if (process.env.NODE_ENV === 'development' && animationSettings.typeSpeed < 10) {
      // Set the full text immediately without animation
      setSteps(prevSteps => {
        const newSteps = [...prevSteps];
        newSteps[stepIndex] = {
          ...newSteps[stepIndex],
          currentDescription: originalText
        };
        return newSteps;
      });
      
      // Call the callback after a short delay
      setTimeout(() => {
        if (callback) callback();
      }, 100);
      
      return;
    }
    
    // Normal typing animation for production or when animation is desired
    const typing = () => {
      if (i < originalText.length) {
        // Get the current character
        const currentChar = originalText.charAt(i);
        
        setSteps(prevSteps => {
          const newSteps = [...prevSteps];
          // Set the description to the substring up to the current position
          // This prevents character duplication issues
          newSteps[stepIndex] = {
            ...newSteps[stepIndex],
            currentDescription: originalText.substring(0, i + 1)
          };
          return newSteps;
        });
        
        // Increment after adding the character
        i++;
        
        // Schedule the next character
        typingRef.current = setTimeout(typing, animationSettings.typeSpeed);
      } else if (callback) {
        callback();
      }
    };
    
    // Start the typing animation immediately
    typing();
  };
  
  // Animate a single step
  const animateStep = (stepIndex) => {
    // First set to progress
    setSteps(prevSteps => {
      const newSteps = [...prevSteps];
      newSteps[stepIndex] = {
        ...newSteps[stepIndex],
        state: 'progress'
      };
      return newSteps;
    });
    
    // For the first step, make the API call
    if (stepIndex === 0) {
      // Log the current state of validatedUrl and the ref
      // Get current URL values
      
      // Use the ref value which is always up-to-date
      const domain = urlRef.current || 'example.com';
      // Process domain
      
      // Call the API and proceed based on result
      const processUrl = async () => {
        try {
          // Log the config value to debug
          // Use API URL from config
          
          const response = await axios.post(
            `${config.FETCH_DOMAIN}/api/modify-url`,
            { domain }
          );
          // Process API response
          
          // Extract website domain and file path from the API response
          if (response.data && response.data.botData && response.data.botData.data) {
            const websiteDomain = response.data.botData.data.website;
            const filePath = response.data.filePath;
            
            // Create demo link by combining the website domain and extracting the site name from file path
            let siteName = '';
            if (filePath) {
              // Extract site name from file path (e.g., /var/www/html/open-infotech.com/index.html -> open-infotech.com)
              const filePathParts = filePath.split('/');
              for (let i = 0; i < filePathParts.length; i++) {
                if (filePathParts[i] === 'html' && i + 1 < filePathParts.length) {
                  siteName = filePathParts[i + 1];
                  break;
                }
              }
            }
            
            // Create the full demo link
            const fullDemoLink = websiteDomain ? `https://${websiteDomain}/${siteName || ''}` : '';
            // Demo link created
            
            // Update the demo link state
            setDemoLink(fullDemoLink);
          }
          
          // API call succeeded, proceed with this step
          proceedWithStep(stepIndex);
          
        } catch (error) {
          // Handle error processing URL
          
          // Extract error message from API response if available
          let errorMessage = 'Failed to process URL';
          
          if (error.response && error.response.data) {
            // Check if the API returned a message property
            if (error.response.data.message) {
              errorMessage = error.response.data.message;
            } else if (typeof error.response.data === 'string') {
              // If the response is a string, use it directly
              errorMessage = error.response.data;
            }
          } else if (error.message) {
            // Use the error message from the Error object
            errorMessage = error.message;
          }
          
          // Update the step to show error
          setSteps(prevSteps => {
            const newSteps = [...prevSteps];
            newSteps[stepIndex] = {
              ...newSteps[stepIndex],
              state: 'error',
              currentDescription: `Error: ${errorMessage}`,
              icon: 'bi-x-circle',
              statusClass: 'status-error'
            };
            return newSteps;
          });
          
          // Don't proceed to next steps if there's an error
        }
      };
      
      processUrl();
    } else {
      // For steps other than the first one, proceed normally
      proceedWithStep(stepIndex);
    }
  };
  
  // Function to proceed with a step after any async operations
  const proceedWithStep = (stepIndex) => {
    // After delay, set to active and start typing animation
    const activeTimeout = setTimeout(() => {
      setSteps(prevSteps => {
        const newSteps = [...prevSteps];
        newSteps[stepIndex] = {
          ...newSteps[stepIndex],
          state: 'active'
        };
        return newSteps;
      });
      
      typeText(steps[stepIndex].description, stepIndex, () => {
        // If there's a next step, animate it after a delay
        if (stepIndex < steps.length - 1) {
          const nextStepTimeout = setTimeout(() => {
            // If this is the second step (index 1), scroll smoothly to the next step
            if (stepIndex === 1) {
              // Find the next step element and scroll to it
              const timelineItems = document.querySelectorAll('.activity-item');
              if (timelineItems && timelineItems.length > stepIndex + 1) {
                // Use custom smooth scroll for a slower, smoother animation
                smoothScrollTo(timelineItems[stepIndex + 1], 1800);
              }
            }
            
            animateStep(stepIndex + 1);
          }, animationSettings.nextStepDelay);
          
          timeoutRefs.current.push(nextStepTimeout);
        } else {
          // This is the last step - mark all steps as completed
          setSteps(prevSteps => {
            return prevSteps.map(step => ({
              ...step,
              state: 'active'
            }));
          });
          
          // Use custom smooth scroll for a slower, smoother animation to QR code section
          if (qrCodeSectionRef.current) {
            smoothScrollTo(qrCodeSectionRef.current, 2000);
          }
        }
      });
    }, animationSettings.stepDelay);
    
    timeoutRefs.current.push(activeTimeout);
  };
  
  // Start the animation sequence
  const startAnimation = () => {
    // Reset all steps to default state
    setSteps(timelineSteps.map(step => ({
      ...step,
      state: 'default',
      currentDescription: '', // Start with empty descriptions
      // Override the status and statusClass for animation purposes
      tempStatus: step.status,
      tempStatusClass: step.statusClass
    })));
    
    // Clear any existing timeouts
    timeoutRefs.current.forEach(ref => clearTimeout(ref));
    if (typingRef.current) clearTimeout(typingRef.current);
    if (animationRef.current) clearTimeout(animationRef.current);
    timeoutRefs.current = [];
    
    // Start with the first step
    animateStep(0);
  };
  
  useEffect(() => {
    // Start the automatic animation sequence when component mounts
    startAnimation();
    
    // Start the title edit animation after a delay
    textAnimationRef.current = setTimeout(startEditAnimation, animationSettings.editDelay);
    
    // Clean up timeouts when component unmounts
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
      if (textAnimationRef.current) {
        clearTimeout(textAnimationRef.current);
      }
      timeoutRefs.current.forEach(ref => clearTimeout(ref));
    };
  }, []);

  // Restart animation on button click
  const handleRestartClick = () => {
    startAnimation();
  };

  // This was moved to the top of the component

  return (
    <>
      <Helmet>
        <title>AI Chatbot Implementation Timeline | AI Chat Assist</title>
        <meta name="description" content="Activity timeline showing the AI chatbot implementation process" />
      </Helmet>

      <section className="demo-section py-5 section-padding">
        <div className="container">
        <div className="row justify-content-center py-5">
            <div className="col-md-10 col-lg-10 text-center">
              <div className="demo-header-content">
                <h2 className="demo-section-title">
                  <span className="demo-hero-heading">Hang tight. Crafting your website demo</span>
                  <br />
                  <span className={`demo-hero-highlight ${isEditing ? 'editing' : ''}`}> -  {heroHighlight}</span>
                </h2>
                {/* <p className="demo-description mt-4">We create high-impact websites, brands, and digital products.</p>
                <div className="mt-4">
                  <button className="btn demo-contact-btn">Contact us</button>
                </div> */}
              </div>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-8 col-xl-6">
              <div className="timeline-card">
                <div className="card-body p-4">
                  <h3 className="timeline-title mb-3">AI Chatbot Implementation</h3>
                  
                  <div className="activity-timeline">
                    {steps.map((step, index) => (
                      <div className="activity-item" key={step.id}>
                        <i 
                          className={getIconClassName(`activity-icon ${step.id}`, step.state) + ` bi ${step.icon}`}
                        ></i>
                        <div className="activity-content">
                          <div className="activity-main">
                            <span className="activity-title" style={getTitleStyle(step.state)}>{step.title}</span>
                            {isDescriptionVisible(step.state) && (
                              <span className="activity-description">{step.currentDescription}</span>
                            )}
                          </div>
                          <div className={`activity-status ${getStatusClass(step.statusClass, step.state)}`}>
                            {getStatusText(step.status, step.state)}
                          </div>
                        </div>
                      </div>
                    ))}                  
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        

      </section>

      {/* QR Code Section */}
      {areAllStepsCompleted() && (
        <section className="qr-code-section" ref={qrCodeSectionRef} style={{padding: '0px',marginBottom: '-3rem'}}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="qr-content-wrapper">
                  <div className="row align-items-center">
                    {/* QR Code Column */}
                    <div className="col-md-5 mb-4 mb-md-0 text-center">
                      <div className="phone-frame-container mx-auto" style={{marginTop: '2rem'}}>
                        <div className="phone-frame">
                          <div className="phone-camera"></div>
                          <div className="qr-code-container">
                            <div className="qr-code-corners">
                              <div className="corner top-left"></div>
                              <div className="corner top-right"></div>
                              <div className="corner bottom-left"></div>
                              <div className="corner bottom-right"></div>
                              <img 
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(demoLink || validatedUrl || 'https://aichatassist.com/demo')}`} 
                                alt="Demo QR Code" 
                                className="img-fluid qr-code"
                                style={{ padding: '36px' }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Content Column */}
                    <div className="col-md-7 text-md-start">
                      <div className="success-badge">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        <span>Ready to Launch</span>
                      </div>
                      <h2 className="section-title mb-3">Your AI Chatbot is Ready!</h2>
                      
                      <div className="feature-highlights mb-4">
                        <div className="feature-item">
                          <i className="bi bi-qr-code me-2"></i>
                          <span>Scan the QR code to instantly access your personalized chatbot</span>
                        </div>
                        <div className="feature-item">
                          <i className="bi bi-chat-dots me-2"></i>
                          <span>Engage visitors with intelligent, automated conversations</span>
                        </div>
                        <div className="feature-item">
                          <i className="bi bi-graph-up me-2"></i>
                          <span>Track performance and optimize user engagement</span>
                        </div>
                      </div>
                      
                      <div className="action-buttons" style={{textAlign: 'left'}}>
                        <a href={demoLink || validatedUrl || '#'} className="btn btn-primary me-3" target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-box-arrow-up-right me-2"></i>Launch Demo
                        </a>
                        <div className="d-inline-block share-dropdown">
                          <button className="btn btn-outline-primary cta-btn animated-btn" type="button" id="shareDropdown">
                            <i className="bi bi-share me-2"></i>Share
                          </button>
                          <ul className="dropdown-menu animated-dropdown">
                            <li><a className="dropdown-item social-item" href="#" onClick={(e) => { e.preventDefault(); handleShare('facebook'); }}><i className="bi bi-facebook me-2"></i>Facebook</a></li>
                            <li><a className="dropdown-item social-item" href="#" onClick={(e) => { e.preventDefault(); handleShare('twitter'); }}><i className="bi bi-twitter me-2"></i>Twitter</a></li>
                            <li><a className="dropdown-item social-item" href="#" onClick={(e) => { e.preventDefault(); handleShare('linkedin'); }}><i className="bi bi-linkedin me-2"></i>LinkedIn</a></li>
                            <li><a className="dropdown-item social-item" href="#" onClick={(e) => { e.preventDefault(); handleShare('whatsapp'); }}><i className="bi bi-whatsapp me-2"></i>WhatsApp</a></li>
                            <li><a className="dropdown-item social-item" href="#" onClick={(e) => { e.preventDefault(); handleShare('telegram'); }}><i className="bi bi-telegram me-2"></i>Telegram</a></li>
                            <li><a className="dropdown-item social-item" href="#" onClick={(e) => { e.preventDefault(); handleShare('email'); }}><i className="bi bi-envelope me-2"></i>Email</a></li>
                            <li><hr className="dropdown-divider" /></li>
                            <li><a className="dropdown-item social-item" href="#" onClick={(e) => { e.preventDefault(); handleShare('copy'); }}><i className="bi bi-clipboard me-2"></i>Copy Link</a></li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Demo;

